import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, mergeMap } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { GraphDotnetService, graphql, RemoteFilter, RemoteFilterKind } from 'app/shared/graph';
import { StoreLocalesService } from 'app/shared/store-locales/store-locales.service';

import { EditRemoteFilterDialogComponent, EditRemoteFilterDialogData, EditRemoteFilterDialogResult } from './edit-remote-filter-dialog.component';
import { RemoteFilterService } from './remote-filter.service';

@Component({
    selector: 'admin-store-remote-filter-page.ts',
    templateUrl: './store-remote-filter-page.component.html',
    styleUrls: ['./store-remote-filter-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatFormField, MatIcon, MatPrefix, MatLabel, MatInput, FormsModule, MatButton, MatTable, MatColumnDef, MatHeaderCell, MatCell, MatIconButton, MatMenuTrigger, MatMenu, MatMenuItem, MatHeaderRow, MatRow, ErrorComponent, MatHeaderCellDef, MatCellDef, MatHeaderRowDef, MatRowDef],
})
export class StoreRemoteFilterPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly graphDotnetService = inject(GraphDotnetService);
    private readonly matDialog = inject(MatDialog);
    private readonly remoteFilterService = inject(RemoteFilterService);
    private readonly storeLocalesService = inject(StoreLocalesService);

    public storeId: string;

    readonly labelTranslations = signal<Record<string, Record<string, string>>>({});
    readonly supportedLocales = signal<string[]>([]);

    @ViewChild('storeRemoteFiltersTable', { static: false })
    remoteFiltersTable: MatTable<RemoteFilter>;
    remoteFilters: RemoteFilter[] = [];
    displayedRemoteFilters: RemoteFilter[] = [];
    displayedColumns: string[] = [
        'thumbnail',
        'visible',
        'order',
        'name',
        'localizedLabel',
        'group',
        'actions',
    ];
    filter: {
        name: string;
    } = {
        name: '',
    };

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.loadRemoteFilters();
        }));
    }

    private loadRemoteFilters() {
        let remoteFilterObservable = this.remoteFilterService.loadRemoteFilters(this.storeId);
        let localizedLabelsObservable = remoteFilterObservable.pipe(mergeMap(x => this.storeLocalesService.getStoreLocalizedLabelsAsRecord(this.storeId, x.map(r => this.getLocalizationKey(r.name)))));
        this.subscribeWithGenericSavingErrorHandling(
            forkJoin([
                remoteFilterObservable,
                localizedLabelsObservable,
                this.storeLocalesService.listStoreSupportedLocales(this.storeId),
            ]),
            ([remoteFilters, labelTranslations, supportedLocales]) => {
                this.labelTranslations.set(labelTranslations);
                this.supportedLocales.set(supportedLocales);
                this.remoteFilters = remoteFilters;
                this.updateDisplayedRemoteFilters();
            },
        );
    }

    getLocalizationKey(remoteFilterName: string) {
        return 'shared.filters.' + remoteFilterName;
    }

    updateFilter() {
        this.updateDisplayedRemoteFilters();
    }

    private updateDisplayedRemoteFilters() {
        this.displayedRemoteFilters = this.remoteFilters
            .filter(s => this.isRemoteFilterVisible(s))
            .sort((a, b) => {
                return (a.group ?? '').localeCompare(b.group ?? '');
            });
        this.remoteFiltersTable.renderRows();
    }

    private isRemoteFilterVisible(f: RemoteFilter) {
        if (this.filter.name) {
            if (f.name.toLowerCase().includes(this.filter.name.toLowerCase())) {
                return true;
            } else if (f.alias && f.alias.toLowerCase().includes(this.filter.name.toLowerCase())) {
                return true;
            }
            return false;
        }
        return true;
    }

    remoteFilterId(i: number, remoteFilter: RemoteFilter) {
        return remoteFilter.id;
    }

    openCreateFilterDialog() {
        const dialogRef = this.matDialog.open<EditRemoteFilterDialogComponent, EditRemoteFilterDialogData, EditRemoteFilterDialogResult>(EditRemoteFilterDialogComponent, {
            minWidth: '100vw',
            height: '100vh',
            data: {
                mode: 'create',
                storeId: this.storeId,
                supportedLocales: this.supportedLocales(),
            } as EditRemoteFilterDialogData,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.pageStatus = PageStatus.saving;
            let mutation: string;
            const { labelTranslations, kind, ...rest } = result;
            const variable = { input: { ...rest, storeId: this.storeId } };
            switch (result.kind) {
                case RemoteFilterKind.Generic:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreateGenericRemoteFilterInput!) {
                        createGenericRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
                case RemoteFilterKind.PerfectlyClear:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreatePerfectlyClearRemoteFilterInput!) {
                        createPerfectlyClearRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
                case RemoteFilterKind.PicsartRemoveBackground:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreatePicsartRemoveBackgroundRemoteFilterInput!) {
                        createPicsartRemoveBackgroundRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
                case RemoteFilterKind.PicsartEffects:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreatePicsartEffectsRemoteFilterInput!) {
                        createPicsartEffectsRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
                case RemoteFilterKind.PicsartStyleTransfer:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreatePicsartStyleTransferRemoteFilterInput!) {
                        createPicsartStyleTransferRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
                case RemoteFilterKind.PicsartLaserEngraving:
                    mutation = graphql`mutation CreateRemoteFilters($input: CreatePicsartLaserEngravingRemoteFilterInput!) {
                        createPicsartLaserEngravingRemoteFilter(input: $input) {
                            id
                        }
                    }`;
                    break;
            }
            forkJoin([
                this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.getLocalizationKey(result.name), labelTranslations),
                this.graphDotnetService.query(mutation, variable),
            ]).subscribe({
                next: _v => {
                    this.pageStatus = PageStatus.loaded;
                    this.loadRemoteFilters();
                },
                error: error => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = error;
                },
            });
        });
    }

    openEditRemoteFilter(remoteFilter: RemoteFilter) {
        const dialogRef = this.matDialog.open<EditRemoteFilterDialogComponent, EditRemoteFilterDialogData, EditRemoteFilterDialogResult>(EditRemoteFilterDialogComponent, {
            minWidth: '100vw',
            height: '100vh',
            data: {
                mode: 'edit',
                storeId: this.storeId,
                remoteFilter: remoteFilter,
                labelTranslations: this.labelTranslations()[this.getLocalizationKey(remoteFilter.name)],
                supportedLocales: this.supportedLocales(),
            } as EditRemoteFilterDialogData,
        });
        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.pageStatus = PageStatus.saving;
            let mutation: string;
            let translations = result.labelTranslations;
            const variable = {
                input: {
                    name: result.name,
                    alias: result.alias,
                    group: result.group,
                    visible: result.visible,
                    data: result.data,
                    order: result.order,
                    id: remoteFilter.id,
                },
            };
            switch (result.kind) {
                case RemoteFilterKind.Generic:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdateGenericRemoteFilterInput!) {
                        updateGenericRemoteFilter(input: $input)
                    }`;
                    break;
                case RemoteFilterKind.PerfectlyClear:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdatePerfectlyClearRemoteFilterInput!) {
                        updatePerfectlyClearRemoteFilter(input: $input)
                    }`;
                    break;
                case RemoteFilterKind.PicsartRemoveBackground:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdatePicsartRemoveBackgroundRemoteFilterInput!) {
                        updatePicsartRemoveBackgroundRemoteFilter(input: $input)
                    }`;
                    break;
                case RemoteFilterKind.PicsartEffects:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdatePicsartEffectsRemoteFilterInput!) {
                        updatePicsartEffectsRemoteFilter(input: $input)
                    }`;
                    break;
                case RemoteFilterKind.PicsartStyleTransfer:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdatePicsartStyleTransferRemoteFilterInput!) {
                        updatePicsartStyleTransferRemoteFilter(input: $input)
                    }`;
                    break;
                case RemoteFilterKind.PicsartLaserEngraving:
                    mutation = graphql`mutation UpdateRemoteFilters($input: UpdatePicsartLaserEngravingRemoteFilterInput!) {
                        updatePicsartLaserEngravingRemoteFilter(input: $input)
                    }`;
                    break;
            }
            forkJoin([
                this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.getLocalizationKey(remoteFilter.name), translations),
                this.graphDotnetService.query(mutation, variable),
            ]).subscribe({
                next: _v => {
                    this.pageStatus = PageStatus.loaded;
                    this.loadRemoteFilters();
                },
                error: error => {
                    this.pageStatus = PageStatus.saveError;
                    this.pageError = error;
                },
            });
        });
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    async uploadRemoteFilterThumbnail(remoteFilter: RemoteFilter, files: FileList) {
        if (files.length !== 1) {
            return;
        }

        let file = files[0];
        const contentBase64 = await blobToBase64(file);
        this.subscribeWithGenericSavingErrorHandling(
            this.remoteFilterService.setRemoteFilterThumbnail({
                remoteFilterId: remoteFilter.id,
                contentBase64: contentBase64,
                mimeType: file.type,
            }),
            () => {
                this.loadRemoteFilters();
            },
        );
    }

    removeRemoteFilterThumbnail(remoteFilter: RemoteFilter) {
        this.subscribeWithGenericSavingErrorHandling(
            this.remoteFilterService.setRemoteFilterThumbnail({
                remoteFilterId: remoteFilter.id,
            }),
            () => {
                this.loadRemoteFilters();
            },
        );
    }

    protected readonly Object = Object;
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const raw = (reader.result as string);
            const separator = raw.indexOf(',');
            resolve(raw.substring(separator === -1 ? 0 : separator + 1) as string);
        };
        reader.readAsDataURL(blob);
    });
}
